import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom"
import { AnnouncementOutlined, ChevronLeft, ChevronRight } from '@mui/icons-material';

import { setDocumentAcknowledged } from "shared/services/railDocumentsApi";
import { RailDocumentPendingActions } from 'shared/models/api/models';
import InfoModal, { ExitCancelModel } from 'components/ui/InfoModal';
import { ClarifyModal } from './DocumentClarify';
import { FeedbackModal } from './DocumentFeedback';
import './Viewer.css'

import {
    Input,
    Button
} from 'reactstrap';
import { MyDocumentsDocumentViewCBTC, MyDocumentsDocumentViewTraditional } from '../../ui/DocumentRules';
import { getUserExistingRulebookMode } from '../../../shared/store/applicationState';
import { RulebookModeEnum, RulebookNameToEnum } from '../../../shared/enums/rulebook-mode-enum';


interface AcknowledgePanelProps {
    documentId: number;
    pendingActions: RailDocumentPendingActions;
    displayAcknowledgePanel: boolean;
}

export const AcknowledgePanel = (props: AcknowledgePanelProps) => {
    const [shrinkAcknowledgePanel, setShrinkAcknowledgePanel] = useState(false);
    const [understoodChecked, setUnderstoodChecked] = useState(false);
    const [understoodUncheckedWarning, setUnderstoodUncheckedWarning] = useState(false);
    const [clarificationModalDisplayed, setClarificationModalDisplayed] = useState(false);
    const [acknowledgeSentModalDisplayed, setAcknowledgeSentModalDisplayed] = useState(false);
    const [feedbackModalDisplayed, setFeedbackModalDisplayed] = useState(false);
    const [AcknowledgementErrorModalDisplayed, setAcknowledgementErrorModalDisplayed] = useState(false);
    const [feedbackRecordedModalDisplayed, setFeedbackRecordedModalDisplayed] = useState(false);
    const [clarifyRecordedModalDisplayed, setClarifyRecordedModalDisplayed] = useState(false);
    const [clarifyEnabled, setClarifyEnabled] = useState(false);
    const [acknowledgeRequired, setAcknowledgeRequired] = useState(false);

    const navigate = useNavigate();

    const commentMaxLength = 500;

    const expandAcknowledgePanel = () => {
        setShrinkAcknowledgePanel(false);
    }

    const toggleShrinkAcknowledgePanel = () => {
        setShrinkAcknowledgePanel(!shrinkAcknowledgePanel);
    }

    const acknowledgeAction = () => {
        if (!understoodChecked) {
            setUnderstoodUncheckedWarning(true);
            return;
        }

        sendAcknowledgement();
    }

    const sendAcknowledgement = () => {
        setDocumentAcknowledged(props.documentId)
            .then(async (response) => {
                if (response === true) {
                    acknowledgeActionResult();
                }
                else {
                    throw (response);
                }
            })
            .catch((e) => {
                console.error(e);
                displaySendAcknowledgementError();
            });
    }

    const acknowledgeActionResult = () => {
        setAcknowledgeSentModalDisplayed(true);
    }

    const displaySendAcknowledgementError = () => {
        setAcknowledgementErrorModalDisplayed(true);
    }

    const clarificationAction = () => {
        setClarificationModalDisplayed(true);
    }

    const naviateToDocuments = () => {
        setAcknowledgeSentModalDisplayed(false);
        const mode = getUserExistingRulebookMode();
        if (RulebookNameToEnum(mode) === RulebookModeEnum.Traditional) {
            navigate(MyDocumentsDocumentViewTraditional.url);
        } else if (RulebookNameToEnum(mode) === RulebookModeEnum.CBTC) {
            navigate(MyDocumentsDocumentViewCBTC.url);
        } else {
            console.log('Error: Could not get user current mode - navigation failed'); 
        }
        
    }

    useEffect(() => {
        setUnderstoodChecked(false);
        setClarifyEnabled(props.pendingActions.clarify);
        setAcknowledgeRequired(props.pendingActions.acknowledge);
    }, [props]);



    const Modals = () => {
        return (
            <>
                <InfoModal
                    text={'Please tick the box to \n acknowledge the document.'}
                    isOpen={understoodUncheckedWarning}
                    closeAction={() => { setUnderstoodUncheckedWarning(false); }} />

                <InfoModal
                    text={'An error occurred. Please try again.'}
                    isOpen={AcknowledgementErrorModalDisplayed}
                    closeAction={() => { setAcknowledgementErrorModalDisplayed(false); }} />

                <ClarifyModal
                    documentId={props.documentId}
                    commentMaxLength={commentMaxLength}
                    isOpen={clarificationModalDisplayed}
                    closeAction={() => { setClarificationModalDisplayed(false); }}
                    submitAction={() => { setClarificationModalDisplayed(false); setClarifyRecordedModalDisplayed(true); setClarifyEnabled(false); }} />

                <ExitCancelModel
                    isOpen={clarifyRecordedModalDisplayed}
                    text={'Your clarification request has been recorded and \n sent to your supervisor.'}
                    actionButtonText={'Back to documents'}
                    action={naviateToDocuments}
                    closeButtonText={'OK'}
                    closeAction={() => { setClarifyRecordedModalDisplayed(false); }} />

                <FeedbackModal
                    documentId={props.documentId}
                    commentMaxLength={commentMaxLength}
                    isOpen={feedbackModalDisplayed}
                    closeAction={() => { setFeedbackModalDisplayed(false); setAcknowledgeRequired(false); }}
                    submitAction={() => { setFeedbackModalDisplayed(false); setFeedbackRecordedModalDisplayed(true); }} />

                <ExitCancelModel
                    isOpen={feedbackRecordedModalDisplayed}
                    text={'Your feedback has been recorded and sent to \n your supervisor.'}
                    actionButtonText={'Back to documents'}
                    action={naviateToDocuments}
                    closeButtonText={'OK'}
                    closeAction={() => { setFeedbackRecordedModalDisplayed(false); setAcknowledgeRequired(false); }} />

                <ExitCancelModel
                    isOpen={acknowledgeSentModalDisplayed}
                    text={'You have successfully acknowledged the document.\n Would you like to send feedback to the admin?'}
                    actionButtonText={'Back to documents'}
                    action={naviateToDocuments}
                    closeButtonText={'Add feedback'}
                    closeAction={() => { setAcknowledgeSentModalDisplayed(false); setFeedbackModalDisplayed(true); }} />
            </>
        );
    }

    const AcknowledgeRow = () => {
        return (
            <div className="pt-4 row">
                <span className="col-1" >
                    <Input type="checkbox" id="chkAck" className="check-view-acknowledge"
                        checked={understoodChecked}
                        onChange={(e) => setUnderstoodChecked(e.target.checked)} />
                </span>
                <label htmlFor="chkAck" className="col-10 col-md-7 text-wrap">I have read and understood this document.</label>
                <span className="col-lg-3">
                    <Button className="btn-primary" onClick={acknowledgeAction}>Acknowledge</Button>
                </span>
                <label className="col-1" />
            </div>
        );
    }

    const ClarifyRow = () => {
        return (
            <div className="pt-4 row">
                <label className="col-1" />
                <label className="col-7">I would like to request more information.</label>
                <span className="col-lg-3">
                    <Button onClick={clarificationAction} disabled={clarifyEnabled == false} >Clarification Needed</Button>
                </span>
                <label className="col-1" />
            </div>
        );
    }

    const AcknowledgeTooltip = () => {
        return (
            !props.displayAcknowledgePanel ?
                <div className="pl-1 tooltiptext acknowledge-reminder-prompt-popup">
                    <div className="p-3 pl-4 pr-4 acknowledge-reminder-prompt-text">
                        <div className="p-2">This document requires acknowledgment.</div>
                        <div className="p-2">Read to the end to acknowledge.</div>
                    </div>
                </div>
                :
                <div onClick={expandAcknowledgePanel}
                    className={`acknowledge-panel-expand-helper` +
                        `${shrinkAcknowledgePanel ? " cursor-pointer" : ""}`} />
        );
    }

    const ShrinkToggle = () => {
        return (
            <div className="p-2 acknowledge-panel-shrink-toggle cursor-pointer" onClick={toggleShrinkAcknowledgePanel}>
                {shrinkAcknowledgePanel ?
                    <ChevronRight />
                    :
                    <ChevronLeft />
                }
            </div>
        );
    }

    return (
        acknowledgeRequired == true ?
            <div className={`acknowledge-reminder-prompt` + `${props.displayAcknowledgePanel ? "" : " cursor-help"}`}>
                <Modals />
                <div className="notification-left-icon tooltip">
                    <div className={
                        `acknowledge-panel-frame` +
                        `${props.displayAcknowledgePanel ? " acknowledge-panel-display" : ""}` +
                        `${shrinkAcknowledgePanel ? " acknowledge-panel-display-shrink" : ""}`}>
                        <div className="acknowledge-panel-content">
                            <div className="acknowledge-panel-content-panel container">
                                <AcknowledgeRow />
                                <ClarifyRow />
                            </div>
                        </div>
                        <ShrinkToggle />
                    </div>
                    <AcknowledgeTooltip />
                    <AnnouncementOutlined fontSize="large"></AnnouncementOutlined>
                </div>
            </div>
            :
            <></>
    );
}
